import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, finalize, retry, throwError, timer } from 'rxjs';
import { TelegramService } from '@infrastructure/telegram';
import { BalanceFacade } from '@feature/balance/facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthApiService } from '@infrastructure/api/auth';
import { MaintenanceService } from '@infrastructure/maintenance';
import { environment } from '../environment/environment';
import { LanguageFacade } from '@feature/language/facade';
import { TonConnectService } from '@infrastructure/ton';
import { TonConnectUI } from '@tonconnect/ui';
import { IConfirmTonDto } from '@domain/deposits';
import { BadRequestError, TonConnectError, UserRejectsError, WalletNotConnectedError } from '@tonconnect/sdk';
import { DepositsApiService } from '@infrastructure/api/deposits';
import { MaintenancePopupComponent } from '@layout/maintenance-popup/maintenance-popup.component';
import { FirstDepositBannerService } from '@infrastructure/first-deposit-banner';
import { PreloaderComponent } from '../../libs/ui/preloader';
import AOS from 'aos';
import { ToastrService } from 'ngx-toastr';
import { AppLoadingService } from '@infrastructure/app-loading';

const AUTH_RETRY_COUNT = 1;
const AUTH_RETRY_DELAY_MS = 1200;

@UntilDestroy()
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, PreloaderComponent, MaintenancePopupComponent],
  templateUrl: './app.html',
})
export class App implements OnInit {
  readonly #appLoading = inject(AppLoadingService);
  readonly #balanceFacade = inject(BalanceFacade);
  readonly #router = inject(Router);
  readonly #telegram = inject(TelegramService);
  readonly #authApi = inject(AuthApiService);
  readonly #maintenance = inject(MaintenanceService);
  readonly #languageFacade = inject(LanguageFacade);
  readonly #tonConnect = inject(TonConnectService);
  readonly #depositApi = inject(DepositsApiService);
  readonly #toastr = inject(ToastrService);
  readonly #firstDepositBanner = inject(FirstDepositBannerService);

  protected readonly isLoading = this.#appLoading.isLoading;
  protected readonly maintenancePopup = this.#maintenance.popup;
  protected readonly maintenanceRetrying = this.#maintenance.retrying;

  public ngOnInit() {
    AOS.init({
      once: true,
      duration: 600,
      easing: 'ease-out',
    });
    this.#languageFacade.initialize();
    this.#router.events
      .pipe(
        untilDestroyed(this),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        requestAnimationFrame(() => AOS.refreshHard());
      });
    this.#telegram?.tg?.onEvent('invoiceClosed', (obj: any) => {
      if (obj?.status === 'paid') {
        this.#firstDepositBanner.markDepositCompleted();
        this.#balanceFacade.loadBalance();
      }
    });

    this.#authenticateOnStartup();

    const tonConnectUI = new TonConnectUI({
      manifestUrl: environment.TON_CONNECT_MANIFEST_URL,
    });

    this.#tonConnect.handleConnect$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        tonConnectUI.openModal();
      },
    });

    this.#tonConnect.handleTransaction$.pipe(untilDestroyed(this)).subscribe({
      next: async transaction => {
        try {
          const result = await tonConnectUI.sendTransaction(transaction.request);

          console.log('✅ Transaction sent:', result);

          const confirmDto: IConfirmTonDto = {
            boc: result.boc,
            ton_deposit_id: transaction.tonDepositId,
          };

          this.#confirmDeposit(confirmDto);
        } catch (error) {
          console.error('❌ Transaction failed or cancelled:', error);
          this.#toastr.error(this.#getTonConnectErrorMessage(error));
        }
      },
    });

    this.#tonConnect.handleDisconnect$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        tonConnectUI.disconnect();
        this.#tonConnect.walletAddress = null;
      },
    });

    tonConnectUI.onStatusChange(
      wallet => {
        this.#tonConnect.walletAddress = wallet?.account?.address ?? null;
      },
      error => {
        console.error('TON Connect status error:', error);
      }
    );
  }

  protected retryMaintenance(): void {
    if (this.maintenanceRetrying()) {
      return;
    }

    this.#maintenance.startRetry();
    this.#authenticate({
      finish: () => this.#maintenance.finishRetry(),
    });
  }

  protected dismissMaintenance(): void {
    this.#maintenance.hide();
  }

  #authenticateOnStartup(): void {
    this.#appLoading.startBootstrap();
    this.#authenticate({
      finish: () => this.#appLoading.finishBootstrap(),
    });
  }

  #authenticate(options: { finish: () => void }): void {
    this.#authApi
      .authenticate({ init_data: this.#telegram.initData || environment.INIT_DATA })
      .pipe(
        untilDestroyed(this),
        retry({
          count: AUTH_RETRY_COUNT,
          delay: error => {
            if (!this.#maintenance.isBackendUnavailableError(error)) {
              return throwError(() => error);
            }

            return timer(AUTH_RETRY_DELAY_MS);
          },
        }),
        finalize(() => {
          options.finish();
        })
      )
      .subscribe({
        error: error => {
          if (this.#maintenance.isBackendUnavailableError(error)) {
            this.#maintenance.showFallbackPopup();
            return;
          }

          console.error('[PG][Auth][bootstrap-error]', error);
        },
      });
  }

  #confirmDeposit(confirmDto: IConfirmTonDto): void {
    this.#depositApi
      .confirmTonDeposit(confirmDto)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: response => {
          console.log('Deposit confirmed:', response);
        },
        error: error => {
          console.error('Error confirming deposit:', error);
          this.#toastr.error(error?.error?.detail || 'Не удалось подтвердить TON-транзакцию');
        },
      });
  }

  #getTonConnectErrorMessage(error: unknown): string {
    if (error instanceof UserRejectsError) {
      return 'Транзакция была отменена в кошельке';
    }

    if (error instanceof WalletNotConnectedError) {
      return 'Кошелек не подключен. Подключите его заново и повторите попытку';
    }

    if (error instanceof BadRequestError) {
      return 'Некорректные параметры TON-транзакции или истёк срок её действия';
    }

    if (error instanceof TonConnectError) {
      return error.message;
    }

    if (error instanceof Error) {
      const message = error.message.toLowerCase();

      if (message.includes('not sent') || message.includes('cancelled')) {
        return 'Транзакция не была отправлена';
      }

      if (message.includes('manifest')) {
        return 'Ошибка загрузки TON Connect manifest';
      }

      if (message.includes('wallet')) {
        return 'Не удалось открыть или использовать кошелек';
      }
    }

    return 'Транзакция отменена или не была отправлена';
  }
}
